// ABOUTME: One platform surface's mockup of a post option, drawn entirely from the
// backend's platform spec table: media frame and fit, avatar shape, caption cut
// and "more" label, the safe zone the platform's chrome covers on full-screen
// video, and the profile-grid crop. No per-platform branches live here.
import { useState, type CSSProperties } from 'react'
import { Bookmark, Heart, MessageCircle, Send } from 'lucide-react'
import {
  previewAssetUrl,
  type PreviewMediaItem,
  type PreviewSurfaceSpec,
  type PreviewVariantSurface,
} from '@/api/previews'
import { cn } from '@/lib/utils'

interface PlatformMockupProps {
  spec: PreviewSurfaceSpec
  surface: PreviewVariantSurface
  media: PreviewMediaItem[]
  businessName: string
}

function Avatar({ name, shape }: { name: string; shape: PreviewSurfaceSpec['avatar'] }) {
  return (
    <span
      aria-hidden
      className={cn(
        'bg-primary text-primary-foreground flex h-8 w-8 shrink-0 items-center justify-center text-xs font-semibold',
        shape === 'circle' ? 'rounded-full' : 'rounded-md'
      )}
    >
      {name.trim().charAt(0).toUpperCase()}
    </span>
  )
}

function MediaItem({ item, spec }: { item: PreviewMediaItem; spec: PreviewSurfaceSpec }) {
  const style: CSSProperties = { objectFit: spec.fit }
  if (item.kind === 'video') {
    // Full-screen surfaces draw their own chrome over the video, so they play on
    // tap like the apps do instead of showing the browser's controls.
    return (
      <video
        className="h-full w-full"
        style={style}
        src={previewAssetUrl(item.url)}
        poster={item.poster_url ? previewAssetUrl(item.poster_url) : undefined}
        preload="none"
        playsInline
        controls={!spec.caption_overlay}
        onClick={(e) => {
          if (!spec.caption_overlay) return
          const video = e.currentTarget
          if (video.paused) void video.play()
          else video.pause()
        }}
      />
    )
  }
  return (
    <img
      className="h-full w-full"
      style={style}
      src={previewAssetUrl(item.url)}
      alt=""
      draggable={false}
    />
  )
}

function Caption({
  surface,
  spec,
  overlay,
}: {
  surface: PreviewVariantSurface
  spec: PreviewSurfaceSpec
  overlay: boolean
}) {
  const [expanded, setExpanded] = useState(false)
  const text = expanded ? surface.caption : surface.caption_shown
  return (
    <p
      data-testid="mockup-caption"
      className={cn(
        'text-sm leading-snug break-words whitespace-pre-line',
        overlay ? 'text-white [text-shadow:var(--text-shadow-overlay)]' : 'text-foreground'
      )}
    >
      {text}
      {surface.caption_truncated && !expanded && (
        <>
          {spec.more_label.startsWith('…') ? '' : '… '}
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className={cn('font-medium', overlay ? 'text-white/80' : 'text-muted-foreground')}
          >
            {spec.more_label}
          </button>
        </>
      )}
    </p>
  )
}

function SafeZone({ spec }: { spec: PreviewSurfaceSpec }) {
  const z = spec.safe_zone
  const pct = (n: number) => `${n * 100}%`
  return (
    <div
      data-testid="safe-zone"
      aria-hidden
      className="pointer-events-none absolute border border-dashed border-white/50"
      style={{ top: pct(z.top), right: pct(z.right), bottom: pct(z.bottom), left: pct(z.left) }}
    />
  )
}

export function PlatformMockup({ spec, surface, media, businessName }: PlatformMockupProps) {
  const [index, setIndex] = useState(0)
  const overlay = spec.caption_overlay
  const shown = media.slice(0, spec.max_media)
  const current = shown[index]
  const gridSrc = shown[0]?.kind === 'video' ? shown[0].poster_url : shown[0]?.url
  const frameStyle: CSSProperties = { aspectRatio: `${spec.frame[0]} / ${spec.frame[1]}` }
  const header = (
    <div className={cn('flex items-center gap-2', overlay ? 'text-white' : 'px-3 py-2')}>
      <Avatar name={businessName} shape={spec.avatar} />
      <span className="truncate text-sm font-semibold">{businessName}</span>
    </div>
  )

  return (
    <article
      data-testid={`mockup-${spec.key}`}
      className="border-border bg-card w-full max-w-[340px] overflow-hidden rounded-2xl border shadow-sm"
    >
      {!overlay && header}
      {current && (
        <div className="relative w-full overflow-hidden bg-black" style={frameStyle}>
          <MediaItem item={current} spec={spec} />
          {overlay && (
            <>
              <SafeZone spec={spec} />
              <div
                className="absolute flex flex-col items-center gap-4 text-white"
                style={{ right: '3%', bottom: `${spec.safe_zone.bottom * 100 + 2}%` }}
                aria-hidden
              >
                <Heart className="h-6 w-6" />
                <MessageCircle className="h-6 w-6" />
                <Send className="h-6 w-6" />
                <Bookmark className="h-6 w-6" />
              </div>
              <div
                className="absolute space-y-1"
                style={{
                  left: `${spec.safe_zone.left * 100}%`,
                  right: `${(spec.safe_zone.right + 0.04) * 100}%`,
                  bottom: '6%',
                }}
              >
                {header}
                <Caption surface={surface} spec={spec} overlay />
              </div>
            </>
          )}
          {shown.length > 1 && (
            <>
              <span className="absolute top-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white">
                {index + 1}/{shown.length}
              </span>
              <div className="absolute inset-x-0 bottom-2 flex justify-center gap-1.5">
                {shown.map((item, i) => (
                  <button
                    key={item.url}
                    type="button"
                    aria-label={`Show image ${i + 1}`}
                    onClick={() => setIndex(i)}
                    className={cn(
                      'h-1.5 w-1.5 rounded-full',
                      i === index ? 'bg-white' : 'bg-white/50'
                    )}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
      {!overlay && (
        <div className="px-3 py-2">
          <Caption surface={surface} spec={spec} overlay={false} />
        </div>
      )}
      <footer className="border-border flex items-center justify-between gap-3 border-t px-3 py-2">
        <span className="text-muted-foreground text-xs font-medium">{spec.label}</span>
        {spec.grid_crop && gridSrc && (
          <span className="text-muted-foreground flex items-center gap-2 text-xs">
            Grid
            <span
              data-testid="grid-crop"
              className="block w-9 overflow-hidden rounded-sm bg-black"
              style={{ aspectRatio: `${spec.grid_crop[0]} / ${spec.grid_crop[1]}` }}
            >
              <img className="h-full w-full object-cover" src={previewAssetUrl(gridSrc)} alt="" />
            </span>
          </span>
        )}
      </footer>
    </article>
  )
}
